import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const PricingContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${props => props.theme.spacing.xxl};
  background-color: ${props => props.theme.colors.background};
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.primary}, 
    ${props => props.theme.colors.secondary}
  );
  padding: ${props => props.theme.spacing.xxl} 0;
  color: white;
  text-align: center;
  min-height: 30vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PricingHeader = styled(motion.h1)`
  font-size: 3rem;
  color: white;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xxl};
`;

const PlanCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const PlanTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const PlanPrice = styled.p`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.25rem;
`;

const HourlyRate = styled.p`
  font-size: 0.9rem;
  font-style: italic;
  color: ${props => props.theme.colors.textLight};
  margin-top: 0;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${props => props.theme.spacing.md} 0;
  flex-grow: 1;
`;

const FeatureItem = styled.li`
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};
`;

const ActionButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem;
  font-size: 1rem;
  cursor: pointer;
  margin-top: auto;

  &:hover {
    background-color: ${props => props.theme.colors.secondary};
  }
`;

const plans = [
  {
    id: 1,
    title: "Basic",
    price: "$500",
    hourlyRate: "$40/hr",
    features: [
      "Simple site (1-3 pages)",
      "Responsive design",
      "1 round of revisions"
    ],
  },
  {
    id: 2,
    title: "Standard",
    price: "$1,500",
    hourlyRate: "$50/hr",
    features: [
      "Up to 6 pages",
      "Contact form integration",
      "Custom UI design",
      "Performance optimization",
      "2 rounds of revisions"
    ],
  },
  {
    id: 3,
    title: "Premium",
    price: "$3,500+",
    hourlyRate: "$75/hr",
    features: [
      "10+ pages or eCommerce",
      "Backend integration",
      "Custom animations",
      "Unlimited revisions"
    ],
  },
];

function PricingPage() {
  const navigate = useNavigate();

  return (
    <PricingContainer>
      <HeroSection>
        <PricingHeader
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Pricing Plans
        </PricingHeader>
      </HeroSection>
      <PlansGrid>
        {plans.map((plan, i) => (
          <PlanCard
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.2 }}
          >
            <PlanTitle>{plan.title}</PlanTitle>
            <PlanPrice>{plan.price}</PlanPrice>
            <HourlyRate>{plan.hourlyRate}</HourlyRate>
            <PlanFeatures>
              {plan.features.map((feature, idx) => (
                <FeatureItem key={idx}>{feature}</FeatureItem>
              ))}
            </PlanFeatures>
            <ActionButton onClick={() => navigate('/contact')}>
              Contact Me
            </ActionButton>
          </PlanCard>
        ))}
      </PlansGrid>
    </PricingContainer>
  );
}

export default PricingPage;
